import type { MessageRecord } from "@iluvatar/global/src/typings/users";
import { useMemo } from "react";
import {
  NOTIFICATION_COLLECTION,
  MESSAGES_COLLECTION,
} from "@iluvatar/global/src/constants";
import {
  InAppNotification,
  NotificationKind,
} from "@iluvatar/global/src/typings/notifications";
import { useAuthenticationContext } from "@/contexts/authentication/authentication.context";
import type { QuerySpec } from "@/services/firebase-documents.runtime";
import { useQuerySnap } from "./useSubscription.v2";

/** The newest unread event is sufficient for a badge and a read-through boundary. */
export function useNotifications(kind: NotificationKind, chatId?: string) {
  const { uid } = useAuthenticationContext();
  const queryPrefix =
    kind === "messages" ? MESSAGES_COLLECTION : NOTIFICATION_COLLECTION;
  const source = useMemo<QuerySpec | undefined>(() => {
    if (!uid) {
      return;
    }
    return {
      collection: queryPrefix,
      filters: [
        ...(kind === "messages"
          ? []
          : ([
              ["type", "==", "inApp"],
              ["kind", "==", kind],
            ] as QuerySpec["filters"])),
        ["recipientUid", "==", uid],
        ["readAt", "==", null],
        ...(chatId ? ([["chatId", "==", chatId]] as QuerySpec["filters"]) : []),
      ],
      order: ["createdAt", "desc"],
      limit: 1,
    };
  }, [uid, kind, chatId, queryPrefix]);
  const [events, state, error] = useQuerySnap<
    InAppNotification | MessageRecord
  >(JSON.stringify([queryPrefix, "unread", kind, chatId]), source);
  return {
    uid,
    hasUnread: Boolean(events?.length),
    latest: events?.[0],
    state,
    error,
  };
}
